import { FormEvent, useEffect, useState } from "react";
import { ComplianceTrainingRecord } from "../../common/ComplianceTraining";
import { useUpdateComplianceTrainingRecordMutation } from "../../redux/api/complianceTraining";
import { useListComplianceTrainingsQuery } from "../../redux/api/complianceTraining";
import { Edit } from "@mui/icons-material";
import { Button, Dialog, InputLabel, MenuItem, Select, Typography } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";

interface ComplianceTrainingRecordUpdateModalProps {
    record: ComplianceTrainingRecord;
    onUpdate: () => void;
}

const ComplianceTrainingRecordUpdateModal = ({ record, onUpdate }: ComplianceTrainingRecordUpdateModalProps) => {
    const [open, setOpen] = useState(false);
    const [trainingId, setTrainingId] = useState(record.trainingId);
    const [issueDate, setIssueDate] = useState(record.issueDate ?? "");
    const [expirationDate, setExpirationDate] = useState(record.expirationDate ?? "");

    const [updateRecord] = useUpdateComplianceTrainingRecordMutation();
    const { data: trainings, isLoading, isFetching } = useListComplianceTrainingsQuery();

    useEffect(() => {
        setTrainingId(record.trainingId);
        setIssueDate(record.issueDate ?? "");
        setExpirationDate(record.expirationDate ?? "");
    }, [record]);

    const handleSubmit = async (event: FormEvent) => {
        event.preventDefault();
        const response = await updateRecord({
            id: record.id,
            complianceTraining: { id: trainingId },
            issueDate,
            expirationDate,
        });
        if (!("error" in response)) {
            setOpen(false);
            onUpdate();
        }
    };

    return (
        <>
            <Button className="blue-link-button" onClick={() => setOpen(true)}>
                <Edit />
                Edit
            </Button>
            <Dialog
                open={open}
                onClose={() => setOpen(false)}
                aria-labelledby="modal-title"
                className="modal"
            >
                <div className="modal-header mb-2">
                    <Typography id="modal-title" variant="h3">
                        Update Record
                    </Typography>
                    <Button onClick={() => setOpen(false)} className="grey-link-button">
                        <CloseIcon />
                    </Button>
                </div>
                <form onSubmit={handleSubmit}>
                    <div className="form-narrow">
                        {!isFetching && !isLoading && (
                            <div>
                                <InputLabel id="trainingLabel">Compliance Training</InputLabel>
                                <Select
                                    labelId="trainingLabel"
                                    id="training"
                                    notched={false}
                                    value={trainingId}
                                    label="Compliance Training"
                                    onChange={(e) => setTrainingId(+e.target.value)}
                                >
                                    {trainings
                                        ?.filter((t) => !t.deleted)
                                        .map((t) => {
                                            const requiresDocButNone = t.documentRequired && !record.fileUrl;
                                            return (
                                                <MenuItem key={t.id} value={t.id} disabled={requiresDocButNone}>
                                                    {t.name}{requiresDocButNone ? " (requires document)" : ""}
                                                </MenuItem>
                                            );
                                        })}
                                </Select>
                            </div>
                        )}
                        <label htmlFor="issueDate">
                            <span>Issue Date:</span>
                            <input
                                type="date"
                                id="issueDate"
                                value={issueDate}
                                onChange={(e) => setIssueDate(e.target.value)}
                            />
                        </label>
                        <label htmlFor="expirationDate">
                            <span>Expiration Date:</span>
                            <input
                                type="date"
                                id="expirationDate"
                                value={expirationDate}
                                onChange={(e) => setExpirationDate(e.target.value)}
                            />
                        </label>
                    </div>
                    <input
                        type="submit"
                        value="Update Record"
                        className="blue-button-small modal-button"
                    />
                </form>
            </Dialog>
        </>
    );
};

export default ComplianceTrainingRecordUpdateModal;
